import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { Connection } from '@apideck/vault-js';
import { StatementsAccountingIntegrationService } from '@app/modules/statements/services/statements-accounting-integration.service';
import { StatementsFacade } from '@app/modules/businesses/services/statements.facade';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { PusherService } from '@app/shared/services/pusher/pusher.service';
import { ConfettiComponent } from '@app/ui/confetti/confetti.component';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, delay, filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DialogPreviewAccountingComponent } from '../dialog-preview-accounting/dialog-preview-accounting.component';

@Component({
  selector: 'sbnb-statements-accounting-connection-form',
  templateUrl: './statements-accounting-connection-form.component.html',
  styles: [],
  providers: [StatementsAccountingIntegrationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatementsAccountingConnectionFormComponent extends OnDestroyComponent implements OnInit {
  @Input() editing = false;
  @ViewChild('confetti') confetti: ConfettiComponent;

  public formState$ = this.statementsAccountingIntegrationService.formState$;
  public loading$ = this.formState$.pipe(map((state) => state.loading));
  public saving$ = new BehaviorSubject<boolean>(false);
  public integrationForm = this.fb.group({
    currency: new UntypedFormControl(''),
  });
  public formInitialized$ = new BehaviorSubject<boolean>(false);
  public currentStep = 0;
  public connectionError$ = new BehaviorSubject<boolean>(null);
  public connection$ = this.statementsAccountingIntegrationService.connection$;
  public deleting$ = this.statementsAccountingIntegrationService.deletingConnection$.pipe(
    map((deleting) => deleting[this.statementsAccountingIntegrationService.connectionId]),
    tap((deleting) => {
      if (deleting?.success) {
        this.redirectToAccounting();
      }
    })
  );
  public hasPreviewed = new BehaviorSubject<boolean>(false);

  private includedPropertiesIds$ = this.formInitialized$.pipe(
    filter((initialized) => initialized),
    switchMap(() =>
      this.integrationForm.get('properties').valueChanges.pipe(
        startWith(this.integrationForm.get('properties').value),
        map((formProperties) =>
          Object.entries(formProperties)
            .filter(([, value]) => (value as any).include)
            .map(([key]) => Number(key))
        )
      )
    )
  );

  // Hide owners that are not associated with any included properties in the UI
  public eligibleOwners$ = this.includedPropertiesIds$.pipe(
    switchMap((selectedPropertyIds) =>
      this.formState$.pipe(
        filter((state) => state.data !== null),
        map((state) =>
          Object.entries(state.data.owners.fieldMap)
            .map(([, owner]) => owner)
            .filter((owner) => selectedPropertyIds.some((id) => owner.propertyIds.includes(id)))
        )
      )
    )
  );

  constructor(
    private statementsAccountingIntegrationService: StatementsAccountingIntegrationService,
    private statementsFacade: StatementsFacade,
    private fb: UntypedFormBuilder,
    private pusher: PusherService,
    private router: Router,
    private dialog: MatDialog
  ) {
    super();
  }

  ngOnInit(): void {
    // Initialize form data
    this.statementsAccountingIntegrationService
      .initializeFormData(this.statementsAccountingIntegrationService.connectionId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.setUpForm();
      });

    if (!this.editing) {
      this.listenForPusherEvents();
    }

    // Disable owners that are not associated with any included properties, so that their value is not submitted
    this.eligibleOwners$.pipe(takeUntil(this.destroyed$)).subscribe((owners) => {
      Object.keys((this.integrationForm.get('owners') as UntypedFormGroup).getRawValue()).forEach((ownerId) => {
        if (!owners.some((owner) => owner.id === Number(ownerId))) {
          this.integrationForm.get('owners').get(ownerId).disable();
        } else {
          this.integrationForm.get('owners').get(ownerId).enable();
        }
      });
    });
  }

  private setUpForm(): void {
    this.formState$
      .pipe(
        filter((state) => state.data !== null),
        take(1)
      )
      .subscribe((state) => {
        // Set up accounts form group
        this.integrationForm.addControl(
          'accounts',
          this.fb.group(
            Object.entries(state.data.accounts.fieldMap).reduce((acc, [reference, field]) => {
              acc[reference] = new UntypedFormControl(field.value);
              return acc;
            }, {})
          )
        );

        // Set up properties form group
        this.integrationForm.addControl(
          'properties',
          this.fb.group(
            Object.entries(state.data.properties.fieldMap).reduce((acc, [id, field]) => {
              acc[id] = this.fb.group({
                include: new UntypedFormControl(!this.editing ? true : Boolean(field.value)),
                class: new UntypedFormControl(field.value),
              });
              return acc;
            }, {})
          )
        );

        // Set up owners form group
        this.integrationForm.addControl(
          'owners',
          this.fb.group(
            Object.entries(state.data.owners.fieldMap).reduce((acc, [id, field]) => {
              acc[id] = new UntypedFormControl(field.value);
              return acc;
            }, {})
          )
        );

        this.formInitialized$.next(true);
      });
  }

  private listenForPusherEvents() {
    this.pusher.accountingConnectionUpdate$
      .pipe(
        tap(() => this.saving$.next(false)),
        tap(
          (data: {
            type: 'failed_onboarding' | 'finished_onboarding' | 'ledger_accounts_synced';
            connection: Connection;
          }) => {
            if (data.type === 'finished_onboarding' && data.connection.enabled) {
              this.confetti.makeItRain();
            }

            if (data.type === 'failed_onboarding') {
              this.connectionError$.next(true);
            }
          }
        ),
        delay(5000),
        takeUntil(this.destroyed$)
      )
      .subscribe((data: { connection: Connection }) => {
        if (data.connection.enabled) {
          this.redirectToAccounting();
        }
      });
  }

  public save() {
    this.saving$.next(true);

    this.statementsFacade
      .saveAccountingConnection(this.statementsAccountingIntegrationService.connectionId, this.dataForSubmission())
      .pipe(
        catchError((error) => {
          this.saving$.next(false);
          this.connectionError$.next(error);
          return EMPTY;
        })
      )
      .subscribe((response) => {
        if (this.editing) {
          this.saving$.next(false);
          this.redirectToAccounting();
        }
      });
  }

  public next() {
    this.currentStep++;
  }

  public back() {
    this.currentStep--;
  }

  public deleteConnectionPrompt() {
    this.connection$.pipe(take(1)).subscribe((connection) => {
      this.statementsAccountingIntegrationService.deleteConnectionPrompt(connection);
    });
  }

  public redirectToAccounting() {
    this.router.navigate(['/operations/statements/accounting']);
  }

  private dataForSubmission() {
    const formState = this.statementsAccountingIntegrationService.formState$.getValue();
    const data = {
      currency: this.integrationForm.get('currency').value,
      properties: Object.entries(this.integrationForm.get('properties').value)
        .filter(([propertyId, fieldValue]: [string, any]) => fieldValue.include)
        .map(([propertyId, fieldValue]: [string, any]) => ({
          id: Number(propertyId),
          tracking_category: fieldValue.class ? Number(fieldValue.class) : undefined,
          use_default: !fieldValue.class ? true : false,
        })),
      accounts: Object.entries(this.integrationForm.get('accounts').value).map(([accountReference, fieldValue]) => ({
        reference: accountReference,
        account: fieldValue ? Number(fieldValue) : undefined,
        use_default: !fieldValue ? true : false,
      })),
      owners: Object.entries(this.integrationForm.get('owners').value).map(([ownerId, fieldValue]) => {
        const ownerFieldMap = formState.data?.owners.fieldMap[ownerId];
        return {
          id: Number(ownerId),
          use_default: !fieldValue ? true : false,
          external_id: fieldValue ? Number(fieldValue) : undefined,
          type: ownerFieldMap?.externalType,
        };
      }),
    };
    return data;
  }

  public openPreviewDialog() {
    this.hasPreviewed.next(true);
    const dialogRef = this.dialog.open(DialogPreviewAccountingComponent, {
      width: '720px',
      maxHeight: '90vh',
      data: {
        connectionId: this.statementsAccountingIntegrationService.connectionId,
        accountMapping: this.dataForSubmission(),
      },
    });
  }
}
